/**
 * Platform Admin Access Control
 *
 * Gates the operator-only pages (Template Builder, Field Catalog, ISMS Journey
 * admin) — as distinct from a tenant's own administrators.
 *
 * This is an email allowlist because the Cognito user pools currently have no
 * groups defined (verified 2026-08-03 against both eu-central-1_Bx1u9SmmN and
 * eu-central-1_2EWROWLVg). Once operator groups exist in Cognito, replace this
 * with a group-membership check on the ID token and drop the allowlist.
 */

// Kept in sync with the server-side gate in
// lambda/prod-nimbus-config/lambda_function.py (is_platform_admin) — if the two
// lists disagree, the UI either hides pages the API would allow or shows pages it
// will reject. Add operators explicitly; do not infer membership from who happens
// to exist in the Cognito pool.
const PLATFORM_ADMIN_EMAILS = [
  'charles.wilson@mercell.com',
  'charles.wilson+test@mercell.com',
];

export const isPlatformAdmin = (userEmail: string | undefined): boolean => {
  if (!userEmail) return false;
  return PLATFORM_ADMIN_EMAILS.includes(userEmail.toLowerCase());
};

export const requirePlatformAdmin = (userEmail: string | undefined): void => {
  if (!isPlatformAdmin(userEmail)) {
    throw new Error('Unauthorized: platform admin access required');
  }
};

/**
 * Reads the super-admin flag cached at login from the ID token's cognito:groups
 * claim (see LoginPage / AuthCallbackPage).
 *
 * Known gap: no Cognito groups exist in either user pool yet, so this is
 * currently always false and the Super Admin page is unreachable. Creating the
 * group is an infrastructure change (rubicon-platform-infrastructure), not a
 * frontend one.
 */
export const isSuperAdmin = (): boolean => {
  return localStorage.getItem('isSuperAdmin') === 'true';
};
